'use client';

import { useEffect } from 'react';

import { getApiUrl } from '@/shared/utils/getApiUrl';
import { WEBMCP_TOOL_DEFINITIONS } from './webmcp-manifest';

/**
 * WebMCP: runtime-регистрация инструментов для AI-агентов
 * (navigator.modelContext — проект стандарта Web Model Context).
 * В браузерах без поддержки — тихий no-op. Инструменты только читают
 * публичные данные каталога, ничего не изменяют (readOnlyHint: true).
 * Декларативная копия манифеста отдаётся в SSR HTML (см. webmcp-script.tsx).
 */

type McListItem = {
  id: number;
  wordId: string;
  name: string;
  price: number;
  finalPrice: number;
  rate?: number;
  reviewsCount?: number;
  tags?: string[];
};

const toPlainText = (blocks: unknown): string => {
  if (!Array.isArray(blocks)) return '';
  const walk = (nodes: any[]): string =>
    nodes
      .map((n) =>
        typeof n?.text === 'string'
          ? n.text
          : Array.isArray(n?.children)
            ? walk(n.children)
            : '',
      )
      .join(' ');
  return walk(blocks).replace(/\s+/g, ' ').trim();
};

const mcSummary = (mc: McListItem) => ({
  wordId: mc.wordId,
  name: mc.name,
  priceRub: mc.finalPrice ?? mc.price,
  rating: mc.rate ?? null,
  reviewsCount: mc.reviewsCount ?? 0,
  url: `https://enjoy-toys.store/masterclasses/${mc.wordId}/`,
});

async function fetchMcList(): Promise<McListItem[]> {
  const res = await fetch(getApiUrl('/masterclasses'));
  if (!res.ok) throw new Error(`masterclasses: HTTP ${res.status}`);
  const json = await res.json();
  return json.results ?? [];
}

const EXECUTORS: Record<string, (_args: any) => Promise<unknown>> = {
  async search_masterclasses({ query }: { query: string }) {
    const list = await fetchMcList();
    const q = String(query ?? '').toLowerCase();
    const results = list
      .filter(
        (mc) =>
          mc.name?.toLowerCase().includes(q) ||
          mc.tags?.some((t) => t.toLowerCase().includes(q)),
      )
      .map(mcSummary);
    return { results };
  },
  async get_masterclass({ wordId }: { wordId: string }) {
    const res = await fetch(
      getApiUrl(`/masterclasses/${encodeURIComponent(wordId)}`),
    );
    if (!res.ok) throw new Error(`masterclass: HTTP ${res.status}`);
    const mc = await res.json();
    return {
      ...mcSummary(mc),
      description: toPlainText(mc.description),
      materials: toPlainText(mc.materials),
    };
  },
  async list_masterclasses() {
    const list = await fetchMcList();
    return { results: list.map(mcSummary) };
  },
};

const buildRuntimeTools = () =>
  WEBMCP_TOOL_DEFINITIONS.map((def) => ({
    ...def,
    execute: EXECUTORS[def.name],
  }));

export const WebMCPTools = () => {
  useEffect(() => {
    const modelContext = (navigator as any).modelContext;
    if (!modelContext) return;

    const tools = buildRuntimeTools();
    try {
      if (typeof modelContext.registerTool === 'function') {
        const unregisters = tools.map((tool) =>
          modelContext.registerTool(tool),
        );
        return () => {
          unregisters.forEach((un: unknown) => {
            if (typeof un === 'function') un();
          });
        };
      }
      if (typeof modelContext.provideContext === 'function') {
        modelContext.provideContext({ tools });
      }
    } catch {
      // WebMCP — экспериментальный API: ошибки регистрации не должны
      // влиять на работу сайта
    }
  }, []);

  return null;
};
